import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Select } from "antd";
import "./SearchableSelect.css";

function selectValueForOption(o) {
  if (o == null || typeof o !== "object") return undefined;
  const v = o.value;
  if (v !== undefined && v !== null && v !== "") return v;
  if (o.id !== undefined && o.id !== null && o.id !== "") return o.id;
  return undefined;
}

function resolvedAntdValue(externalValue, options) {
  const opts = Array.isArray(options) ? options : [];
  if (opts.length === 0) return undefined;

  const empty =
    externalValue === undefined ||
    externalValue === null ||
    externalValue === "" ||
    (typeof externalValue === "string" && externalValue.trim() === "");
  if (empty) return undefined;

  const s = String(externalValue);

  for (const o of opts) {
    const sv = selectValueForOption(o);
    if (sv !== undefined && String(sv) === s) return sv;
  }

  const byName = opts.find((o) => String(o.name) === s);
  if (byName) return selectValueForOption(byName);

  return undefined;
}

const SearchableSelect = ({
  value,
  onChange,
  name,
  label,
  options,
  required,
  disabled,
  error,
  isMulti,
  onDropdownVisibleChange,
}) => {
  const optionRows = useMemo(
    () => (Array.isArray(options) ? options : []),
    [options],
  );

  const antdOptions = useMemo(() => {
    return optionRows
      .map((o) => {
        const value = selectValueForOption(o);
        return {
          value,
          label:
            typeof o.name === "string" && o.name.trim()
              ? o.name
              : `#${o.id ?? o.value}`,
        };
      })
      .filter(
        (row) =>
          row.value !== undefined && row.value !== null && row.value !== "",
      );
  }, [optionRows]);

  const antdControlledValue = useMemo(() => {
    if (!isMulti) return resolvedAntdValue(value, optionRows);

    const arr = Array.isArray(value)
      ? value
      : value === undefined || value === null || value === ""
        ? []
        : [value];
    return arr
      .map((v) => resolvedAntdValue(v, optionRows))
      .filter((v) => v !== undefined);
  }, [value, optionRows, isMulti]);

  const handleOnChange = (val) => {
    if (isMulti) {
      if (!Array.isArray(val) || val.length === 0) {
        return onChange([], []);
      }
      const selectedOption = val.map((v) =>
        optionRows.find(
          (o) =>
            selectValueForOption(o) !== undefined &&
            String(selectValueForOption(o)) === String(v),
        ),
      );
      return onChange(val, selectedOption);
    }

    if (val === undefined || val === null) {
      return onChange(val, { id: "", name: "" });
    }

    const selectedOption = optionRows.find(
      (o) =>
        selectValueForOption(o) !== undefined &&
        String(selectValueForOption(o)) === String(val),
    );
    return onChange(val, selectedOption ?? { id: "", name: "" });
  };

  return (
    <div className="search-input-container" style={{ color: "black" }}>
      <label
        className="input-label"
        htmlFor={name}
        style={{ fontFamily: "Gotham Rounded" }}
      >
        {label} {required && "*"}
      </label>
      <Select
        name={name}
        value={
          isMulti
            ? Array.isArray(antdControlledValue) &&
              antdControlledValue.length > 0
              ? antdControlledValue
              : undefined
            : antdControlledValue
        }
        placeholder={`Please select the ${label}`}
        optionFilterProp="label"
        filterOption={(input, option) =>
          String(option?.label ?? "")
            .toLowerCase()
            .includes(String(input).toLowerCase())
        }
        onChange={handleOnChange}
        className="search-input"
        bordered={false}
        showSearch={true}
        allowClear={required ? false : true}
        required={required}
        disabled={disabled}
        mode={isMulti ? "multiple" : undefined}
        style={error.length > 0 ? { border: "1px solid red" } : {}}
        onOpenChange={(open) => onDropdownVisibleChange?.(open)}
        getPopupContainer={() => document.body}
        popupClassName="searchable-select-dropdown-popup"
        styles={{
          popup: {
            root: {
              zIndex: 2000,
            },
          },
        }}
        options={antdOptions}
      />
      <div className="input-error">{error}</div>
    </div>
  );
};

SearchableSelect.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
  ]),
  onChange: PropTypes.func,
  name: PropTypes.string,
  label: PropTypes.string,
  options: PropTypes.array,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  error: PropTypes.string,
  isMulti: PropTypes.bool,
  onDropdownVisibleChange: PropTypes.func,
};

SearchableSelect.defaultProps = {
  value: "",
  onChange: () => {},
  name: "",
  label: "",
  options: [],
  disabled: false,
  required: false,
  error: "",
  isMulti: false,
  onDropdownVisibleChange: undefined,
};

export default SearchableSelect;
